import { useState, useEffect, useCallback } from "react";
import { History, Clock } from "lucide-react";
import { getMasterPricesHistoryByStateCity, getSubscriptionsHistoryByStateCity } from "@/services/branchIncharge";
import { getZoneMasterPricesHistory, getZoneSubscriptionsHistory } from "@/services/zoneManager";
import type { BranchInfo } from "../index";
import ReadOnlyTable from "../ReadOnlyTable";
import { useScreenPermission } from "@/hooks/useScreenPermission";

interface Props { branchInfo: BranchInfo; }

type SubTab = "masterprices" | "subscriptions";

const SUB_TABS: Array<{ id: SubTab; label: string; icon: React.ReactNode; screenKey: string }> = [
  { id: "masterprices",  label: "Master Prices History", icon: <History className="w-4 h-4" />, screenKey: "masterpriceshistory" },
  { id: "subscriptions", label: "Subscriptions History", icon: <Clock   className="w-4 h-4" />, screenKey: "subscriptionplanshistory" },
];

export default function HistoryScreen({ branchInfo }: Props) {
  const mpPerm  = useScreenPermission("masterpriceshistory");
  const subPerm = useScreenPermission("subscriptionplanshistory");

  const permMap: Record<SubTab, boolean> = {
    masterprices:  mpPerm.canView,
    subscriptions: subPerm.canView,
  };

  const visibleTabs = SUB_TABS.filter(t => permMap[t.id]);

  const [activeTab, setActiveTab] = useState<SubTab>("masterprices");

  // Switch to first visible tab if current one becomes hidden
  useEffect(() => {
    if (visibleTabs.length > 0 && !permMap[activeTab]) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [mpPerm.canView, subPerm.canView]);

  // Master Prices History state
  const [mpData,       setMpData]       = useState<any[]>([]);
  const [mpLoading,    setMpLoading]    = useState(false);
  const [mpPage,       setMpPage]       = useState(1);
  const [mpTotalPages, setMpTotalPages] = useState(1);

  // Subscriptions History state
  const [subData,       setSubData]       = useState<any[]>([]);
  const [subLoading,    setSubLoading]    = useState(false);
  const [subPage,       setSubPage]       = useState(1);
  const [subTotalPages, setSubTotalPages] = useState(1);
  const [mpTotalCount, setMpTotalCount] = useState(0);
  const [subTotalCount, setSubTotalCount] = useState(0);

  const loadMp = useCallback(async (p = 1) => {
    setMpLoading(true);
    try {
      const res = branchInfo.Zone_Code
        ? await getZoneMasterPricesHistory(branchInfo.Zone_Code, p)
        : await getMasterPricesHistoryByStateCity(branchInfo.State_Code, branchInfo.City_Code, p);
      if (res.status === 200 && res.data) {
        const d = res.data as any;
        setMpData(d.results ?? (Array.isArray(d) ? d : []));
        setMpTotalPages(d.total_pages ?? 1);
        setMpTotalCount(d.count ?? 0);
      }
    } catch {  }
    finally { setMpLoading(false); }
  }, [branchInfo.Zone_Code, branchInfo.State_Code, branchInfo.City_Code]);

  const loadSub = useCallback(async (p = 1) => {
    setSubLoading(true);
    try {
      const res = branchInfo.Zone_Code
        ? await getZoneSubscriptionsHistory(branchInfo.Zone_Code, p)
        : await getSubscriptionsHistoryByStateCity(branchInfo.State_Code, branchInfo.City_Code, p);
      if (res.status === 200 && res.data) {
        const d = res.data as any;
        setSubData(d.results ?? (Array.isArray(d) ? d : []));
        setSubTotalPages(d.total_pages ?? 1);
        setSubTotalCount(d.count ?? 0);
      }
    } catch {  }
    finally { setSubLoading(false); }
  }, [branchInfo.Zone_Code, branchInfo.State_Code, branchInfo.City_Code]);

  useEffect(() => { loadMp(1); loadSub(1); }, [loadMp, loadSub]);

  const mpColumns = [
    // { key: "Plan_Name",       label: "Plan" },
    { key: "Base_Price",  label: "Price (₹)" },
    // { key: "Monthly_Base_Price",  label: "Monthly Price (₹)" },
    { key: "State",      label: "State" },
    { key: "City",       label: "District" },
    { key: "Updated_At", label: "Updated At", render: (v: string) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { key: "Updated_By", label: "Updated By" },
  ];

  const subColumns = [
    { key: "Plan_Name",       label: "Plan" },
    { key: "Base_Price",  label: "Price (₹)" },
    { key: "Monthly_Base_Price",  label: "Monthly Price (₹)" },
    { key: "State",      label: "State" },
    { key: "City",       label: "District" },
    { key: "Updated_At", label: "Updated At", render: (v: string) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { key: "Updated_By", label: "Updated By" },
  ];

  if (visibleTabs.length === 0) {
    return (
      <div className="flex items-center justify-center py-32">
        <p className="text-gray-500">You don't have permission to view history.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Sub-tab switcher */}
      <div className="flex gap-3 mb-6">
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all border ${
              activeTab === tab.id
                ? "bg-brand-500 text-white border-brand-500 shadow-sm"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "masterprices" && permMap.masterprices && (
        <ReadOnlyTable
          title="Master Prices History"
          subtitle={`${branchInfo.State} · ${branchInfo.City}`}
          icon={<History className="w-5 h-5 text-mlb-green" />}
          data={mpData}
          columns={mpColumns}
          isLoading={mpLoading}
          page={mpPage}
          totalPages={mpTotalPages}
          onPageChange={(p) => { setMpPage(p); loadMp(p); }}
          emptyMessage="No price history found for this region"
          totalCount={mpTotalCount}
        />
      )}

      {activeTab === "subscriptions" && permMap.subscriptions && (
        <ReadOnlyTable
          title="Subscriptions History"
          subtitle={`${branchInfo.State} · ${branchInfo.City}`}
          icon={<Clock className="w-5 h-5 text-mlb-green" />}
          data={subData}
          columns={subColumns}
          isLoading={subLoading}
          page={subPage}
          totalPages={subTotalPages}
          onPageChange={(p) => { setSubPage(p); loadSub(p); }}
          emptyMessage="No subscription history found for this region"
          totalCount={subTotalCount}
        />
      )}
    </div>
  );
}
